import { useEffect, useRef, type ReactNode, type TextareaHTMLAttributes } from "react";
import { cn } from "./cn";

/** Phase 3 building blocks: dialog, chips, badge, textarea, page header. */

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...rest}
      className={cn(
        "block min-h-24 w-full rounded-lg border border-line bg-surface px-3 py-2 text-base text-ink",
        "focus:border-brand-500 focus:outline focus:outline-2 focus:outline-brand-500/30",
        className,
      )}
    />
  );
}

/**
 * Native <dialog>: focus is trapped and Escape closes it for free, which matters for keyboard
 * and screen-reader users (NFR-8).
 */
export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-labelledby="dialog-title"
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-card border border-line bg-surface p-0 text-ink shadow-xl backdrop:bg-black/40"
    >
      <div className="p-5">
        <h2 id="dialog-title" className="mb-3 text-lg font-semibold">
          {title}
        </h2>
        {children}
      </div>
      {footer ? (
        <div className="flex flex-wrap justify-end gap-2 border-t border-line p-4">{footer}</div>
      ) : null}
    </dialog>
  );
}

/** A row of toggle buttons (filters, tabs). `aria-pressed` tells screen readers which is on. */
export function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string; count?: number }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
          className={cn(
            "min-h-10 rounded-full border px-4 text-sm",
            o.value === value
              ? "border-brand-600 bg-brand-600 text-white"
              : "border-line bg-surface text-ink hover:bg-brand-50",
          )}
        >
          {o.label}
          {o.count !== undefined ? (
            <span className="ml-1 opacity-80" aria-label={`${o.count} items`}>
              ({o.count})
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export type BadgeTone = "neutral" | "info" | "ok" | "warn" | "danger";

export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: ReactNode }) {
  const tones: Record<BadgeTone, string> = {
    neutral: "bg-canvas text-muted border-line",
    info: "bg-brand-50 text-brand-700 border-brand-100",
    ok: "bg-ok/10 text-ok border-ok/30",
    warn: "bg-warn/10 text-warn border-warn/30",
    danger: "bg-danger/10 text-danger border-danger/30",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

export function PageHeader({
  title,
  help,
  actions,
  subtitle,
}: {
  title: string;
  subtitle?: ReactNode;
  help?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold">{title}</h1>
        {subtitle ? <div className="mt-1 text-sm text-muted">{subtitle}</div> : null}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        {help}
      </div>
    </div>
  );
}
